import clsx from 'clsx';
import { Fragment, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { columns } from '@/App/tiles/ForwardAccumulator/parts/RightPanel/Scheduler/columns.ts';
import DataCellRenderer from '@/App/tiles/ForwardAccumulator/parts/RightPanel/Scheduler/Table/DataCellRenderer.tsx';
import TotalLeverage from '@/App/tiles/ForwardAccumulator/parts/RightPanel/Scheduler/Table/TotalLeverage.tsx';
import TotalNotional from '@/App/tiles/ForwardAccumulator/parts/RightPanel/Scheduler/Table/TotalNotional.tsx';
import { useForwardAccumulatorTileId } from '@/App/tiles/ForwardAccumulator/ProductProvider.tsx';
import {
  selectForwardAccumulatorCurrencyData,
  selectForwardAccumulatorOrderedScheduleItemIds,
  selectForwardAccumulatorSettlementFrequencyData,
} from '@/store/forwardAccumulator/selectors.ts';
import type { SchedulerCalendarProperty } from '@/store/forwardAccumulator/utils.ts';
import { useAppSelector } from '@/store/hooks.ts';
import { selectActiveTabZoomedTileId } from '@/store/tabs/slice.ts';

const HEIGHT_SMALL = 142;
const HEIGHT_MEDIUM = 380;

type Props = {
  keepSmall?: boolean;
};

const SchedulerTable = ({ keepSmall = false }: Props) => {
  const { formatMessage } = useIntl();
  const tileId = useForwardAccumulatorTileId();

  const { currency } = useAppSelector((state) => selectForwardAccumulatorCurrencyData(state, tileId));
  const { settlementFrequency } = useAppSelector((state) =>
    selectForwardAccumulatorSettlementFrequencyData(state, tileId),
  );

  const scheduleIds = useAppSelector((state) => selectForwardAccumulatorOrderedScheduleItemIds(state, tileId));
  const isZoomed = useAppSelector(selectActiveTabZoomedTileId) === tileId;

  const ForwardAccuColumns =
    settlementFrequency === 'Maturity'
      ? (columns as SchedulerCalendarProperty[])
      : ([columns[0], 'paymentDate', ...columns.slice(1)] as SchedulerCalendarProperty[]);

  const columnsHeaderLabels = useMemo(
    () =>
      ForwardAccuColumns.map((property) =>
        property === 'deleteIcon'
          ? ''
          : formatMessage(
              { id: `accumulator.scheduler.calendar.${property === 'amount' ? 'notional' : property}` },
              { ccy: currency },
            ),
      ),
    [currency, ForwardAccuColumns, formatMessage],
  );

  const maxHeight = useMemo(() => {
    if (keepSmall) {
      return `${HEIGHT_SMALL}px`;
    }
    if (isZoomed) {
      return 'auto';
    }
    return `${HEIGHT_MEDIUM}px`;
  }, [keepSmall, isZoomed]);

  return (
    <div
      className="mt-2  overflow-y-auto overflow-x-hidden"
      style={{
        maxHeight,
        flexGrow: 1,
      }}
    >
      <table data-e2e="scheduler" className="table table-sm table-bordered bg-lvl1 mb-0">
        <thead data-e2e="header">
          <tr className="sticky-top">
            <th className="text-nowrap bg-lvl1 py-0 border-start-0 border-bottom-0 position-sticky fs-12">#</th>
            {columnsHeaderLabels.map((label, index) => (
              <th
                className={clsx('text-nowrap bg-lvl1 py-0 border-bottom-0 position-sticky fs-12', {
                  'border-end-0': index === ForwardAccuColumns.length - 1,
                })}
                key={label}
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>

        <tbody data-e2e="body">
          {scheduleIds?.map((legId, itemIndex) => (
            <tr key={legId} data-e2e="row" className="fs-12">
              <td className="p-0 border-0">
                <div className="d-flex align-items-center px-2 form-control-sm form-control-plaintext text-nowrap">
                  {itemIndex + 1}
                </div>
              </td>

              <Fragment key={legId}>
                {ForwardAccuColumns.map((property, index) => (
                  <td
                    key={`${property}-${legId}`}
                    className={clsx('p-0', { 'border-end-0': index === ForwardAccuColumns.length - 1 })}
                  >
                    <DataCellRenderer property={property} legId={legId} />
                  </td>
                ))}
              </Fragment>
            </tr>
          ))}
        </tbody>

        <tfoot className="sticky-bottom">
          <tr>
            <td
              className="fs-12 bg-lvl1 border-bottom-0 text-end border-start-0"
              colSpan={ForwardAccuColumns.length - 2}
            >
              Total
            </td>
            <td className="bg-lvl1 border-bottom-0 text-end">
              <TotalNotional />
            </td>
            <td className="bg-lvl1 border-bottom-0 text-end">
              <TotalLeverage />
            </td>
            <td className="bg-lvl1 border-bottom-0 border-end-0" />
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default SchedulerTable;
